import { Injectable } from '@angular/core';
import {
  Firestore,
  Unsubscribe,
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { FirebaseAppService } from '../../../core/services/firebase-app.service';
import { CrmAttachment, CrmMessage } from '../models/crm-message.model';
import { CrmThread } from '../models/crm-thread.model';
import { htmlToPlainText } from '../utils/html-to-text';
import { MessageLogService } from './message-log.service';

@Injectable({
  providedIn: 'root'
})
export class CrmMessagingService {
  private firestore: Firestore;

  constructor(
    private firebaseAppService: FirebaseAppService,
    private messageLogService: MessageLogService
  ) {
    this.firestore = this.firebaseAppService.getFirestore();
  }

  /** Newest threads first. crm_threads holds the whole HubSpot email history
   *  (thousands of threads), so callers that only show the top of the list
   *  pass limitCount instead of downloading every thread. */
  listThreads(callback: (threads: CrmThread[]) => void, limitCount?: number): Unsubscribe {
    const threadsQuery = limitCount
      ? query(collection(this.firestore, 'crm_threads'), orderBy('updatedAt', 'desc'), limit(limitCount))
      : query(collection(this.firestore, 'crm_threads'), orderBy('updatedAt', 'desc'));

    return onSnapshot(
      threadsQuery,
      (snapshot) => {
        callback(snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() } as CrmThread)));
      },
      (error) => {
        console.error('CrmMessagingService.listThreads snapshot error', error);
      }
    );
  }

  /**
   * Scoped version of listThreads() for one contact's email address - used
   * by EmailThreadComponent so a CRM record's Email tab doesn't subscribe to
   * (and download) the entire company-wide crm_threads collection just to
   * filter it down to one contact client-side. Matches threads where the
   * address is the thread's contact (externalAddress) or anyone on it
   * (participants - reply-all, someone else replying in). Addresses are
   * stored lowercase, so the lookup is too. Neither query orders in
   * Firestore (newest-first sort happens here), so no composite index is
   * needed. onError fires if either listener fails, e.g. permission-denied.
   * recordKey ("module:uuid") also matches threads linked to the CRM record
   * itself (records[] - set by the HubSpot email import), so the tab shows
   * emails with any of the company's contacts, not just its main one.
   */
  listThreadsForAddress(
    externalAddress: string | null,
    callback: (threads: CrmThread[]) => void,
    onError?: (error: unknown) => void,
    recordKey?: string | null
  ): Unsubscribe {
    const address = (externalAddress ?? '').trim().toLowerCase();
    const threadsRef = collection(this.firestore, 'crm_threads');
    const byKey = { contact: [] as CrmThread[], participant: [] as CrmThread[], record: [] as CrmThread[] };

    const emit = () => {
      const merged = new Map<string, CrmThread>();
      [...byKey.contact, ...byKey.participant, ...byKey.record].forEach((thread) => merged.set(thread.id, thread));
      const millis = (value: any) => typeof value?.toMillis === 'function' ? value.toMillis() : 0;
      callback([...merged.values()]
        .filter((thread) => thread.channel === 'email')
        .sort((a, b) => millis(b.updatedAt) - millis(a.updatedAt)));
    };
    const listen = (key: keyof typeof byKey, threadsQuery: ReturnType<typeof query>) => onSnapshot(
      threadsQuery,
      (snapshot) => {
        byKey[key] = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...(docSnapshot.data() as object) } as CrmThread));
        emit();
      },
      (error) => {
        console.error('CrmMessagingService.listThreadsForAddress snapshot error', error);
        onError?.(error);
      }
    );

    const unsubscribers: Unsubscribe[] = [];
    if (address) {
      unsubscribers.push(
        listen('contact', query(threadsRef, where('externalAddress', '==', address))),
        listen('participant', query(threadsRef, where('participants', 'array-contains', address)))
      );
    }
    if (recordKey) {
      unsubscribers.push(listen('record', query(threadsRef, where('records', 'array-contains', recordKey))));
    }
    if (!unsubscribers.length) callback([]);

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Older threads the Inbox hasn't loaded (it only holds the newest page),
   * found in Firestore by what the term starts with: the contact's address,
   * its domain or the company name (externalDomain / companyNameLower - set
   * on imported HubSpot threads). Firestore has no substring search, and a
   * range on one field needs no composite index. One-off read, not a listener.
   */
  async searchThreads(term: string, perField = 25): Promise<CrmThread[]> {
    const value = term.trim().toLowerCase();
    if (value.length < 3) return [];
    const threadsRef = collection(this.firestore, 'crm_threads');
    const startsWith = (field: string) => getDocs(query(threadsRef, where(field, '>=', value), where(field, '<=', value + ''), limit(perField)));
    const results = await Promise.allSettled(['externalAddress', 'externalDomain', 'companyNameLower'].map(startsWith));
    const merged = new Map<string, CrmThread>();
    results.forEach((result) => {
      if (result.status !== 'fulfilled') return console.error('CrmMessagingService.searchThreads error', result.reason);
      result.value.docs.forEach((docSnapshot) => merged.set(docSnapshot.id, { id: docSnapshot.id, ...docSnapshot.data() } as CrmThread));
    });
    return [...merged.values()];
  }

  /** Only threads with something unread - the app-wide badge/toast needs
   *  these, not the whole (HubSpot-history-sized) collection. */
  listUnreadThreads(callback: (threads: CrmThread[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(this.firestore, 'crm_threads'), where('unreadCount', '>', 0)),
      (snapshot) => {
        callback(snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() } as CrmThread)));
      },
      (error) => {
        console.error('CrmMessagingService.listUnreadThreads snapshot error', error);
      }
    );
  }

  /** Every thread on one channel - the floating SMS inbox lists all SMS
   *  threads; filtering in Firestore keeps the email history out of it. */
  listThreadsByChannel(channel: CrmThread['channel'], callback: (threads: CrmThread[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(this.firestore, 'crm_threads'), where('channel', '==', channel)),
      (snapshot) => {
        callback(snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() } as CrmThread)));
      },
      (error) => {
        console.error('CrmMessagingService.listThreadsByChannel snapshot error', error);
      }
    );
  }

  /** Single thread doc (unreadCount, readByName, etc.) - listThreads() above
   *  covers the whole collection, this is for a component that only cares
   *  about one thread it already knows the id of. */
  getThread(threadId: string, callback: (thread: CrmThread | null) => void): Unsubscribe {
    return onSnapshot(
      doc(this.firestore, 'crm_threads', threadId),
      (snapshot) => {
        callback(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as CrmThread) : null);
      },
      (error) => {
        console.error('CrmMessagingService.getThread snapshot error', error);
      }
    );
  }

  /** Page size for limitCount below - imported HubSpot threads can hold
   *  hundreds of messages, and every one is a Firestore read. */
  static readonly MESSAGE_PAGE = 50;

  /** Pass limitCount to get only the newest messages (still oldest-first in
   *  the callback); hasMore says older ones exist. Without it, all of them.
   *  One extra message is fetched so a thread of exactly limitCount messages
   *  doesn't offer "Load earlier" for nothing. */
  listMessages(
    threadId: string,
    callback: (messages: CrmMessage[], hasMore: boolean) => void,
    limitCount?: number
  ): Unsubscribe {
    const messagesRef = collection(this.firestore, `crm_threads/${threadId}/messages`);
    const messagesQuery = limitCount
      ? query(messagesRef, orderBy('sentAt', 'desc'), limit(limitCount + 1))
      : query(messagesRef, orderBy('sentAt', 'asc'));

    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        const hasMore = !!limitCount && snapshot.size > limitCount;
        const docs = limitCount ? snapshot.docs.slice(0, limitCount) : snapshot.docs;
        const messages = docs.map(
          (docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() } as CrmMessage)
        );

        // A just-sent message's serverTimestamp() reads as null on this
        // device until the server round-trip confirms it, and Firestore's
        // orderBy sorts null before every real timestamp - so without this,
        // a message you just sent flashes to the top of the thread instead
        // of the bottom. Treat "not resolved yet" as "just now" instead.
        messages.sort((a, b) => this.sentAtMillis(a) - this.sentAtMillis(b));

        callback(messages, hasMore);
      },
      (error) => {
        console.error('CrmMessagingService.listMessages snapshot error', error);
      }
    );
  }

  private sentAtMillis(message: CrmMessage): number {
    const sentAt = message.sentAt as any;
    return typeof sentAt?.toMillis === 'function' ? sentAt.toMillis() : Date.now();
  }

  /**
   * Writes the optimistic outbound message doc only - the caller still has
   * to call CrmEmailSendService/SmsSendService so custm-api-v3 actually
   * dispatches it (via Mailgun or Ringotel) and flips status from
   * "sending" to "sent"/"failed".
   */
  async addOutboundMessage(
    threadId: string,
    senderId: string,
    bodyHtml: string,
    subject: string,
    inReplyTo: string | null,
    to: string[],
    attachments: CrmAttachment[] = [],
    channel: 'email' | 'sms' = 'email'
  ): Promise<string> {
    const messagesRef = collection(this.firestore, `crm_threads/${threadId}/messages`);
    // SMS has no HTML body, subject, or attachments - bodyHtml is left
    // unset so the UI's plainBody branch renders it (identical to how
    // inbound SMS messages, which also never get a bodyHtml field, render).
    const docRef = await addDoc(messagesRef, {
      channel,
      direction: 'outbound',
      senderId,
      // Quick client-side strip for the immediate optimistic preview - the
      // real one (used for the actual sent email and the thread/sidebar
      // preview) comes from the server once send() responds. For SMS,
      // bodyHtml is already plain text, so this is close to a no-op.
      text: htmlToPlainText(bodyHtml),
      ...(channel === 'email' ? { bodyHtml } : {}),
      ...(channel === 'email' ? { subject } : {}),
      status: 'sending',
      // Same value sent to Mailgun as the real In-Reply-To email header -
      // recorded here too so our own thread-nesting display (which reads
      // this field, not the actual outgoing email) can indent it under
      // the right message. Not meaningful for SMS.
      ...(channel === 'email' ? { inReplyTo } : {}),
      // Who this specific send actually went to - a reply-all and a
      // targeted single-recipient reply can nest under the exact same
      // parent message, so indentation alone can't distinguish them.
      to,
      ...(channel === 'email' ? { attachments } : {}),
      sentAt: serverTimestamp()
    });

    // An outbound reply never touches the crm_threads/{threadId} doc itself
    // (only this messages subcollection, then a later status patch from the
    // backend send) - the Message Log's live-refresh ping is keyed off the
    // *thread* collection changing, so it would otherwise never fire for a
    // reply send. Ping it directly here instead of relying on that side effect.
    this.messageLogService.notifyChanged();

    return docRef.id;
  }

  /**
   * Flips an optimistic outbound message to 'failed' when the send request
   * itself was rejected (permission, validation, server error) - the backend
   * never got far enough to patch the status, so without this it would sit
   * on 'sending' forever. Best effort: resolves false if the write is refused.
   */
  async markOutboundFailed(threadId: string, messageId: string): Promise<boolean> {
    try {
      await updateDoc(doc(this.firestore, `crm_threads/${threadId}/messages/${messageId}`), {
        status: 'failed',
        statusUpdatedAt: serverTimestamp()
      });
      return true;
    } catch {
      return false;
    }
  }
}
